import { Button, Divider, Typography } from "@suraasa/placebo-ui"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import api from "api"
import { queries } from "api/queries"
import CourseFallback from "assets/Placeholder/product-image-placeholder.svg"
import AsyncBuilder from "components/AsyncBuilder"
import ErrorPage from "components/ErrorPage"
import FullPageLoading from "components/FullPageLoading"
import { AuthManager } from "features/Authentication/AuthManager"
import {
  getDeliveryMode,
  getProductImage,
} from "features/LearningItems/getProductDisplayConfig"
import { CheckCircleSolid, HeadsetHelp, PlaySolid } from "iconoir-react"
import { useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import routes from "routes"
import { learningItemName, learningItemSlugs } from "utils/constants"
import { downloadInvoice, formatDate, mapErrorsV2 } from "utils/helpers"
import useFormatPrice from "utils/hooks/useFormatPrice"

import { trackPgctlRegistrationEnded } from "../tracking"

const PaymentSuccess = ({
  orderId,
  productSlug,
  isFromLandingPage,
}: {
  orderId: string
  productSlug: string
  isFromLandingPage: boolean
}) => {
  const navigate = useNavigate()
  const formatPrice = useFormatPrice()

  const queryClient = useQueryClient()

  const orderDetails = useQuery({
    queryFn: () => api.orders.getOrderDetails({ urlParams: { id: orderId } }),
    queryKey: queries.orders.orderItem(orderId).queryKey,
  })

  useEffect(() => {
    // Refresh the offer letters API to trigger the onboarding
    queryClient.refetchQueries({
      queryKey: queries.offerLetter.list().queryKey,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const transaction = orderDetails.data?.transactions[0]

  const trackingEndedRef = useRef(false)
  useEffect(() => {
    if (!isFromLandingPage) return
    if (trackingEndedRef.current) return
    if (!transaction?.id) return
    trackingEndedRef.current = true

    const authInfo = AuthManager.getPlatformAuth()
    const email =
      new URLSearchParams(window.location.search).get("email") ||
      authInfo?.user.email ||
      ""

    trackPgctlRegistrationEnded({
      email,
      transactionId: transaction.id,
      status: "success",
    })
  }, [transaction?.id])

  const getReceipt = async () => {
    try {
      const transactionId = transaction?.id
      if (!transactionId) {
        throw new Error("Transaction ID not found")
      }
      const data = await api.orders.getInvoice({
        urlParams: {
          transactionId,
        },
      })
      downloadInvoice(data)
    } catch (e) {
      mapErrorsV2(e)
    }
  }

  if (orderDetails.isLoading) return <FullPageLoading />

  if (orderDetails.isError) return <ErrorPage />
  if (!transaction) return <ErrorPage />

  if (isFromLandingPage) {
    return (
      <div className="relative grid min-h-dvh place-items-center">
        <div className="absolute inset-0 z-0 min-h-screen bg-[linear-gradient(180deg,#D4F8F1_0%,#FEFEFF_13.4%,#F8FAFC_89.31%)] sm:bg-[linear-gradient(180deg,#D4F8F1_0%,#FEFEFF_46.4%,#F8FAFC_100%)]" />
        <div className="relative z-10 flex justify-center p-2 py-5 sm:items-center sm:py-0">
          <div className="flex max-w-md flex-col rounded-3xl sm:border sm:border-onSurface-200 sm:bg-white sm:p-4 sm:shadow-[0px_6px_15px_0px_#0000000D]">
            <CheckCircleSolid className="mx-auto mb-3 size-5 text-success-600" />
            <Typography variant={"title1"} className="mb-1 text-center">
              🎉 You&apos;re in!
            </Typography>
            <Typography variant={"largeBody"} className="mb-4 text-center">
              Your seat in the PgCTL cohort is reserved.
            </Typography>
            <div className="mb-4 flex items-start gap-2 rounded-lg bg-onSurface-100 px-1.5 py-2.5">
              <HeadsetHelp className="size-3 shrink-0 text-primary" />
              <div>
                <Typography variant="strongSmallBody" className="mb-0.5">
                  Our counsellor will give you a call within the next 24 hours.
                </Typography>
                <Typography variant="smallBody">
                  They will guide you through the next steps and answer any
                  questions.
                </Typography>
              </div>
            </div>
            <div className="mb-4">
              <Typography variant="strong" className="mb-2">
                Get a head start
              </Typography>
              <button
                onClick={() => {
                  navigate(routes.learning)
                }}
                className="flex w-full items-center justify-between gap-2 rounded-lg border border-onSurface-200 bg-white px-2 py-1.5 text-start"
              >
                <div>
                  <Typography variant="strong" className="mb-0.5">
                    Introduction to PgCTL
                  </Typography>
                  <Typography variant="smallBody">
                    A quick overview of what&apos;s ahead
                  </Typography>
                </div>
                <div className="grid size-4 shrink-0 place-items-center rounded-full bg-primary">
                  <PlaySolid className="ml-px size-2 text-white" />
                </div>
              </button>
            </div>

            <div className="mb-4 flex flex-row justify-between gap-2">
              <div className="flex flex-col gap-1">
                <Typography
                  variant={"smallBody"}
                  className="text-onSurface-600"
                >
                  Order No.
                </Typography>
                <Typography
                  variant={"smallBody"}
                  className="text-onSurface-600"
                >
                  Transaction ID
                </Typography>
              </div>
              <div className="flex flex-col gap-1">
                <Typography variant={"smallBody"}>
                  {orderDetails.data.serialNumber}
                </Typography>
                <Typography variant={"smallBody"}>
                  {transaction.serialNumber}
                </Typography>
              </div>
            </div>
            <Divider weight="light" className="mb-4 hidden sm:block" />
            <div className="sticky bottom-0 flex flex-col justify-between gap-2 border-t border-onSurface-300 bg-[#fbfcfe] py-2 sm:relative  sm:border-none sm:bg-none sm:py-0 md:flex-row">
              <Button
                onClick={() => {
                  // Refresh the offer letters API to trigger the onboarding
                  queryClient.refetchQueries({
                    queryKey: queries.offerLetter.list().queryKey,
                  })
                  navigate(routes.home)
                }}
                color="black"
                size={"sm"}
                className="w-full"
              >
                Go To Dashboard
              </Button>

              <AsyncBuilder
                handler={async () => {
                  await getReceipt()
                }}
                render={({ onClick, loading }) => (
                  <Button
                    label="Download Invoice"
                    variant={"outlined"}
                    onClick={onClick}
                    disabled={!transaction.id}
                    loading={loading}
                    className="w-full border-onSurface-300 text-onSurface-900"
                    size={"sm"}
                  />
                )}
              />
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="relative min-h-screen">
      <div className="absolute inset-0 z-0 min-h-screen bg-[linear-gradient(180deg,#D4F8F1_0%,#FEFEFF_13.4%,#F8FAFC_89.31%)] sm:bg-[linear-gradient(180deg,#D4F8F1_0%,#FEFEFF_46.4%,#F8FAFC_100%)]" />
      <div className="absolute inset-0 z-10 flex justify-center p-2 sm:items-center">
        <div className="flex max-w-md flex-col rounded-3xl p-4 sm:border sm:border-onSurface-200 sm:bg-white sm:shadow-[0px_6px_15px_0px_#0000000D]">
          <CheckCircleSolid className="mb-3 size-5 text-success-600" />
          <Typography variant={"title1"} className="mb-1">
            {transaction.currency.symbol}
            {formatPrice(transaction.amount)}
          </Typography>
          <Typography variant={"largeBody"} className="mb-6">
            Payment Received on {formatDate(transaction.createdAt)}
          </Typography>
          <div className="flex flex-row gap-2">
            <img
              alt="pgctl"
              onError={({ currentTarget }) => {
                currentTarget.onerror = null
                currentTarget.src = CourseFallback
              }}
              src={getProductImage({
                slug: learningItemSlugs.pgctl,
                defaultImage: CourseFallback,
                deliveryMode: getDeliveryMode(productSlug),
              })}
              className="size-[84px] shrink-0 overflow-hidden rounded-xl border border-surface-100 object-cover"
            />
            <div className="mb-4.5 flex flex-col gap-0.75">
              <Typography variant="strong">{learningItemName.pgctl}</Typography>
              <Typography variant="preTitle" className="text-onSurface-500">
                Registration Fee
              </Typography>
            </div>
          </div>
          <div className="mb-4 flex flex-row gap-2">
            <div className="flex flex-col gap-1">
              <Typography variant={"smallBody"} className="text-onSurface-600">
                Order No.
              </Typography>
              <Typography variant={"smallBody"} className="text-onSurface-600">
                Transaction ID
              </Typography>
            </div>
            <div className="flex flex-col gap-1">
              <Typography variant={"smallBody"}>
                {orderDetails.data.serialNumber}
              </Typography>
              <Typography variant={"smallBody"}>
                {transaction.serialNumber}
              </Typography>
            </div>
          </div>
          <Divider weight="light" className="-mx-4 mb-4 hidden sm:block" />
          <div className="flex flex-col justify-between gap-2 md:flex-row">
            <Button
              label="Continue"
              onClick={() => {
                // Refresh the offer letters API to trigger the onboarding
                queryClient.refetchQueries({
                  queryKey: queries.offerLetter.list().queryKey,
                })
                navigate(routes.home)
              }}
              color="black"
              size={"sm"}
              className="w-full"
            />
            <AsyncBuilder
              handler={async () => {
                await getReceipt()
              }}
              render={({ onClick, loading }) => (
                <Button
                  label="Download Invoice"
                  variant={"outlined"}
                  onClick={onClick}
                  disabled={!transaction.id}
                  loading={loading}
                  className="w-full border-onSurface-300 text-onSurface-900"
                  size={"sm"}
                />
              )}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default PaymentSuccess
